import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

import RouteHelpers from 'helpers/routes';
import PageLoader from 'components/PageLoader/PageLoader';

import App from 'pages/App';
import Account from 'pages/Account';
import Improvements from 'pages/Improvements';
import Measure from 'pages/Measure';
import Trainings from 'pages/Trainings';
import TrainingCatalog from 'pages/TrainingCatalog';
import Tokens from 'pages/Tokens';
import Users from 'pages/Users';
import UsersLogin from 'pages/Users/Login';
import Forbidden from 'pages/Forbidden';
import NotFound from 'pages/NotFound';

const AdminDashboard = lazy(() => import('pages/Admin'));

const AccountHome = lazy(() => import('pages/Account/Home'));
const AccountPhone = lazy(() => import('pages/Account/Phone'));
const AccountPassword = lazy(() => import('pages/Account/Password'));
const AccountExperience = lazy(() => import('pages/Account/Experience'));

const Calibrations = lazy(() => import('pages/Calibrations'));
const CalibrationScoreReport = lazy(() => import('pages/Calibrations/ScoreReport'));
const Certifications = lazy(() => import('pages/Certifications'));
const CoachLearningResources = lazy(() => import('pages/Coach'));
const Dashboard = lazy(() => import('pages/Dashboard'));
const DoubleCodingQueue = lazy(() => import('pages/DoubleCodingQueue'));
const CoachingRoster = lazy(() => import('pages/Improvements/CoachingRoster'));
const EducatorProfile = lazy(() => import('pages/Improvements/EducatorProfile'));
const InvitationsView = lazy(() => import('pages/Invitations/View'));
const LearningResources = lazy(() => import('pages/LearningResources'));

const MeasureHome = lazy(() => import('pages/Measure/Home'));
const MeasureCreate = lazy(() => import('pages/Measure/Create'));
const MeasureView = lazy(() => import('pages/Measure/View'));
const MeasureSummary = lazy(() => import('pages/Measure/Summary'));
const MeasureCompleted = lazy(() => import('pages/Measure/Completed'));
const MeasureObservationVideos = lazy(() => import('pages/Measure/ObservationVideos'));
const MeasureObservationVideosView = lazy(() => import('pages/Measure/ObservationVideos/View'));

const ObservationReport = lazy(() => import('pages/Reports/Observation'));
const CreateReportConfiguration = lazy(() => import('pages/Reports/Observation/Create'));
const EditReportConfiguration = lazy(() => import('pages/Reports/Observation/View'));
const TestReport = lazy(() => import('pages/Reports/Test'));
const TransitionTestReport = lazy(() => import('pages/Reports/Test/TransitionTestReport'));
const Organization = lazy(() => import('pages/Organization'));
const ScoreReport = lazy(() => import('pages/Testing/Reliability/ScoreReport'));
const TestAttempts = lazy(() => import('pages/TestAttempts'));
const TestPreparations = lazy(() => import('pages/TestPreparations'));
const TestPreparationsScoreReport = lazy(() => import('pages/TestPreparations/ScoreReport'));
const TestTemplate = lazy(() => import('pages/TestTemplate'));
const TokenConfirmation = lazy(() => import('pages/TokenConfirmation'));
const TrainerViewTestAttempts = lazy(() => import('pages/Testing/Reliability/TestAttempts'));

const TrainingsHome = lazy(() => import('pages/Trainings/Home'));
const TrainingsEdit = lazy(() => import('pages/Trainings/Edit'));
const TrainingsView = lazy(() => import('pages/Trainings/View'));
const TrainingsCheckout = lazy(() => import('pages/Trainings/View/Checkout'));
const TrainingsCardCheckout = lazy(() => import('pages/Trainings/View/CardCheckout'));
const TrainingsCheckoutCompleted = lazy(() => import('pages/Trainings/View/CheckoutCompleted'));
const TrainingsCancellationPolicy = lazy(() => import('pages/Trainings/CancellationPolicy'));
const TrainingViewDetails = lazy(() => import('components/Trainings/View/Details'));
const TrainingViewParticipants = lazy(() => import('components/Trainings/View/Participants'));
const TrainingViewAttendance = lazy(() => import('components/Trainings/View/Attendance'));

const TrainingCatalogHome = lazy(() => import('pages/TrainingCatalog/Home'));
const TrainingCatalogCreate = lazy(() => import('pages/TrainingCatalog/Create'));

const TrainingHistory = lazy(() => import('pages/TrainingHistory'));
const UserChoiceProductKey = lazy(() => import('pages/UserChoiceProductKey'));
const LearningPathwayProductKey = lazy(() => import('pages/LearningPathwayProductKey'));
const RecertificationProductKey = lazy(() => import('pages/RecertificationProductKey'));

const TokensView = lazy(() => import('pages/Tokens/View'));
const TokensEnter = lazy(() => import('pages/Tokens/Enter'));

const UsersActivateProductKey = lazy(() => import('pages/Users/ActivateProductKey'));
const UsersForgotPassword = lazy(() => import('pages/Users/ForgotPassword'));
const UsersLogout = lazy(() => import('pages/Users/Logout'));
const UsersSetPassword = lazy(() => import('pages/SetPassword'));
const UsersOnboarding = lazy(() => import('pages/Users/Onboarding'));

// Redirects legacy /video/* links (e.g. old emails) to the correct /videos/* path.
function LegacyVideoObsRedirect() {
  const { '*': splat } = useParams();
  return <Navigate to={`/videos/${splat}`} replace />;
}

function ExternalRedirect({ to, preservePath = false }: { to: string; preservePath?: boolean }) {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    const destination = preservePath ? `${to}${pathname}${search}${hash}` : to;
    window.location.replace(destination);
  }, [to, preservePath, pathname, search, hash]);
  return null;
}

export function RoutesConfig() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="admin/*" element={<AdminDashboard />} />

        <Route path="users" element={<Users />}>
          <Route path="login" element={<UsersLogin />} />
          <Route path="logout" element={<UsersLogout />} />
          <Route path="confirmation" element={<UsersSetPassword />} />
          <Route path="reset-password" element={<UsersSetPassword />} />
          <Route path="forgot-password" element={<UsersForgotPassword />} />
          <Route path="resend-email" element={<UsersForgotPassword />} />
          <Route path="activate-product-key" element={<UsersActivateProductKey />} />
          <Route path="onboarding" element={<UsersOnboarding />} />
        </Route>

        <Route path="tokens" element={<Tokens />}>
          <Route index element={<TokensEnter />} />
          <Route path=":token" element={<TokensView />} />
        </Route>

        <Route path="/" element={<App />}>
          <Route index element={<Dashboard />} />

          <Route path="account" element={<Account />}>
            <Route index element={<AccountHome />} />
            <Route path="phone" element={<AccountPhone />} />
            <Route path="password" element={<AccountPassword />} />
            <Route path="experience" element={<AccountExperience />} />
          </Route>

          <Route path="certifications/*" element={<Certifications />} />
          <Route path="coach/learning-resources/*" element={<CoachLearningResources />} />
          <Route path="learning-resources/*" element={<LearningResources />} />

          <Route path="improvement" element={<Improvements />}>
            <Route index element={<CoachingRoster />} />
            <Route path="educator-profile/:id" element={<EducatorProfile />} />
          </Route>

          <Route path="invitations/:token" element={<InvitationsView />} />

          <Route path="measure" element={<Measure />}>
            <Route index element={<MeasureHome />} />
            <Route path="new" element={<MeasureCreate />} />
            <Route path=":id/*" element={<MeasureView />} />
            <Route path=":id/edit" element={<MeasureCreate />} />
            <Route path=":id/completed" element={<MeasureCompleted />} />
            <Route path=":id/summary" element={<MeasureSummary />} />

            <Route path="observation-videos">
              <Route index element={<MeasureObservationVideos />} />
              <Route path=":id" element={<MeasureObservationVideosView />} />
            </Route>
          </Route>

          <Route path="organization/*" element={<Organization />} />
          <Route path="reports/observation">
            <Route index element={<ObservationReport />} />
            <Route path="new" element={<CreateReportConfiguration />} />
            <Route path=":id" element={<EditReportConfiguration />} />
          </Route>
          <Route path="reports/transition/:trainingId" element={<TransitionTestReport />} />
          <Route path="reports/reliability/:trainingId" element={<TestReport />} />
          <Route path="reports/knowledge/:trainingId" element={<TestReport testType="knowledge" />} />

          <Route path="test-attempts/:id">
            <Route index element={<TestAttempts />} />
            <Route path="score-report" element={<ScoreReport />} />
            <Route path="trainer-score-report" element={<TrainerViewTestAttempts />} />
          </Route>

          <Route path="test-preparations/:id">
            <Route index element={<TestPreparations />} />
            <Route path="score-report" element={<TestPreparationsScoreReport />} />
          </Route>

          <Route path="calibrations/*">
            <Route index element={<Calibrations />} />
            <Route path="history" element={<Calibrations isCalibrationsHistory />} />
            <Route path=":id" element={<TestAttempts testType="calibration" />} />
            <Route path=":id/score-report" element={<CalibrationScoreReport />} />
          </Route>

          <Route path="test-template/:id/prep-resources" element={<TestTemplate />} />
          <Route path="token-confirmation" element={<TokenConfirmation />} />

          <Route path="trainings" element={<Trainings />}>
            <Route index element={<TrainingsHome />} />
            <Route path="affiliate-cancellation-policy" element={<TrainingsCancellationPolicy />} />

            <Route path=":id">
              <Route element={<TrainingsView />}>
                <Route index element={<Navigate replace to="details" />} />
                <Route path="details" element={<TrainingViewDetails />} />
                <Route path="participants" element={<TrainingViewParticipants />} />
                <Route path="attendance" element={<TrainingViewAttendance />} />
              </Route>

              <Route path="edit" element={<TrainingsEdit />} />
              <Route path="checkout" element={<TrainingsCheckout />} />
              <Route path="card-checkout" element={<TrainingsCardCheckout />} />
              <Route path="checkout-completed" element={<TrainingsCheckoutCompleted />} />
            </Route>
          </Route>

          <Route path="training-catalog" element={<TrainingCatalog />}>
            <Route index element={<TrainingCatalogHome />} />
            <Route path=":id/new" element={<TrainingCatalogCreate />} />
          </Route>

          <Route path="training-history" element={<TrainingHistory />} />
          <Route path="user-choice-product-key" element={<UserChoiceProductKey />} />
          <Route path="learning-pathway-product-key" element={<LearningPathwayProductKey />} />
          <Route path="recertification-product-key" element={<RecertificationProductKey />} />

          <Route path="double-coding-queue" element={<DoubleCodingQueue />} />

          <Route path="video/*" element={<LegacyVideoObsRedirect />} />

          <Route
            path="videos/*"
            element={<ExternalRedirect to={RouteHelpers.getPath('instant-insights')} preservePath />}
          />

          <Route path="forbidden" element={<Forbidden />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
